import { Group } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DataTableColumn,
    IFilterChip,
    PaginatedProps,
    StandardListViewV2,
    UserPermissionContext,
} from '../../Components/CoreLib/library';
import { triaxReferenceNumberColumnKeySuffixes } from '../../constants/triaxReferenceNumberColumnKeySuffixes';
import { ContactDto, CustomerDto } from '../../dtos';
import {
    useArchiveCustomerBulkDeleteMutation,
    useCustomerBulkArchiveMutation,
    useCustomerBulkUnarchiveMutation,
    useGetCustomerQuery,
    useLazyGetContactQuery,
} from '../../store/generated/generatedApi';
import { usePageTitleSetter } from '../../util';

const defaultPaginationProps: PaginatedProps = {
    sorting: [{ key: 'NAME', direction: 'asc' }],
    page: 0,
    pageSize: 25,
};

const CONTACT_FILTER_CHIP_KEY = 'contact';

const CONTACT_SEARCH_DEBOUNCE_MS = 300;
const CONTACT_SEARCH_MIN_LENGTH = 2;
const CONTACT_SEARCH_PAGE_SIZE = 50;

function getContactDisplayLabel(contact: ContactDto): string {
    const name =
        contact.contactName?.trim() ||
        [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
        'Unnamed';
    const phone = contact.cellPhone || contact.landlinePhone || '';
    return phone ? `${name} (${phone})` : name;
}

export const CustomerListView: FC = () => {
    const navigate = useNavigate();
    const { permissions } = useContext(UserPermissionContext);
    usePageTitleSetter('Customers');

    const canReadContacts = permissions.includes('read:contact');
    const [selectedContact, setSelectedContact] = useState<ContactDto | null>(null);
    const [contactInputValue, setContactInputValue] = useState('');
    const [debouncedContactSearch, setDebouncedContactSearch] = useState('');

    const [fetchContacts, { data: contactSearchResults, isFetching: isContactSearchFetching }] =
        useLazyGetContactQuery();

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedContactSearch(contactInputValue), CONTACT_SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [contactInputValue]);

    useEffect(() => {
        if (!canReadContacts) {
            return;
        }
        const q = debouncedContactSearch.trim();
        if (q.length < CONTACT_SEARCH_MIN_LENGTH) {
            return;
        }
        void fetchContacts({
            searchText: q,
            page: 0,
            pageSize: CONTACT_SEARCH_PAGE_SIZE,
            includeInactive: false,
        });
    }, [canReadContacts, debouncedContactSearch, fetchContacts]);

    const contactOptions = contactSearchResults?.pageResults ?? [];

    const additionalQueryParameters = useMemo(() => {
        if (selectedContact?.customerId) {
            return { customerId: selectedContact.customerId };
        }
        return {};
    }, [selectedContact]);

    const filterChips = useMemo<IFilterChip[]>(() => {
        if (!selectedContact) {
            return [];
        }
        return [{ key: CONTACT_FILTER_CHIP_KEY, text: getContactDisplayLabel(selectedContact) }];
    }, [selectedContact]);

    const handleFilterChipDelete = useCallback((filterChipKey: string) => {
        if (filterChipKey === CONTACT_FILTER_CHIP_KEY) {
            setSelectedContact(null);
            setContactInputValue('');
        }
    }, []);

    const tableColumns: DataTableColumn<CustomerDto>[] = [
        { key: 'name', label: 'Customer Name', sortKey: 'NAME' },
        { key: 'customerNumber', label: 'Customer Number', sortKey: 'CUSTOMER_NUMBER' },
        { key: 'city', label: 'City', sortKey: 'CITY' },
        { key: 'state', label: 'State', sortKey: 'STATE' },
        { key: 'accountStatus', label: 'Account Status', sortKey: 'ACCOUNT_STATUS' },
    ];

    const handleCreateNew = useCallback(() => {
        navigate('/customer/create');
    }, [navigate]);

    const handleTableEdit = useCallback(
        (id: string) => {
            navigate(`/customer/${id}`);
        },
        [navigate]
    );

    const handleRowClick = useCallback(
        (id: string) => {
            navigate(`/customer/${id}/dashboard`);
        },
        [navigate]
    );

    return (
        <StandardListViewV2
            referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
            enableMultiSort
            headerIcon={<Group />}
            headerTitle='Customers'
            breadCrumbs={[{ label: 'Home', navLink: '/' }]}
            defaultPaginationProps={defaultPaginationProps}
            getDataQuery={useGetCustomerQuery}
            isShowActiveToggleVisible
            inactivateMutation={useCustomerBulkArchiveMutation}
            activateMutation={useCustomerBulkUnarchiveMutation}
            deleteMutation={useArchiveCustomerBulkDeleteMutation}
            tableColumns={tableColumns}
            entityNameSingular='Customer'
            permissionName='customer'
            handleAdd={handleCreateNew}
            handleEdit={handleTableEdit}
            handleRowClick={handleRowClick}
            additionalMenuItems={[
                {
                    text: 'View',
                    onClick: (selected: CustomerDto[]) => handleRowClick(selected[0]?.id),
                    enabledCheck: (selected: CustomerDto[]) => selected.length === 1,
                },
            ]}
            paginationOnTop={true}
            stickyTableHeader={true}
            preActionItemSlot={
                canReadContacts ? (
                    <Box sx={{ minWidth: 280, maxWidth: 400 }}>
                        <Autocomplete<ContactDto, false, false, false>
                            size='small'
                            options={contactOptions}
                            value={selectedContact}
                            inputValue={contactInputValue}
                            onChange={(_, newValue) => {
                                setSelectedContact(newValue);
                                if (newValue) {
                                    setContactInputValue(getContactDisplayLabel(newValue));
                                }
                            }}
                            onInputChange={(_, newInputValue, reason) => {
                                if (reason === 'clear') {
                                    setSelectedContact(null);
                                    setContactInputValue('');
                                    return;
                                }
                                if (reason === 'input') {
                                    setContactInputValue(newInputValue);
                                    setSelectedContact(null);
                                }
                                if (reason === 'reset') {
                                    setContactInputValue(newInputValue);
                                }
                            }}
                            filterOptions={(opts) => opts}
                            loading={isContactSearchFetching}
                            getOptionLabel={(option) => getContactDisplayLabel(option)}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            getOptionDisabled={(option) => !option.customerId}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Typography variant='body2'>{getContactDisplayLabel(option)}</Typography>
                                        {option.customer?.name && (
                                            <Typography variant='caption' color='text.secondary'>
                                                {option.customer.name}
                                            </Typography>
                                        )}
                                        {!option.customerId && (
                                            <Typography variant='caption' color='text.secondary'>
                                                No linked customer
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Filter by contact'
                                    placeholder='Name or phone (min. 2 characters)'
                                />
                            )}
                        />
                    </Box>
                ) : undefined
            }
            additionalQueryParameters={additionalQueryParameters}
            filterChips={filterChips}
            handleFilterChipDelete={handleFilterChipDelete}
            noDataMessage={
                selectedContact?.customerId ? 'No customers match this contact filter.' : undefined
            }
        />
    );
};
